/**
 * Google Ads conversion reporting, gated on the **marketing** consent category.
 *
 * The compliance baseline is still Consent Mode: the `ad_*` signals are
 * defaulted per region before the Google tag loads (see the root route, built
 * from `consentRegions.ts`), so a visitor in a deny region is untracked whether
 * or not the surface ever renders. The consent record only lifts that denial.
 *
 * `reportConversion` no-ops when marketing consent is not in force or when the
 * tag never loaded — a blocked tag must never break entry into a room.
 */

import { activeConsent, CONSENT_VERSION } from "@/engine/diagnostics/consent";
import {
  consentRequiredCountries,
  fetchRegionRule,
} from "@/engine/support/consentRegions";

export {
  consentRequiredCountries,
  isConsentRequiredRegion,
} from "@/engine/support/consentRegions";

/** The country codes whose visitors must make an explicit choice. */
export const CONSENT_COUNTRIES: string[] = consentRequiredCountries();

/** The Google Ads account this app reports to. */
export const ADS_TAG_ID = "AW-18408033947";

/** The Google tag, loaded once the page has hydrated. */
export const ADS_TAG_SRC = `https://www.googletagmanager.com/gtag/js?id=${ADS_TAG_ID}`;

/** Run in the head: queues every call until the tag itself arrives. */
export const ADS_TAG_STUB = `window.dataLayer = window.dataLayer || []; window.gtag = window.gtag || function () { window.dataLayer.push(arguments); }; gtag('js', new Date()); gtag('config', '${ADS_TAG_ID}');`;

/**
 * One entry per conversion action registered in the account. The labels are
 * publishable identifiers, not secrets: they only say which action an event
 * belongs to.
 */
export const CONVERSION_LABELS = {
  "room-created": `${ADS_TAG_ID}/M8iiCK_Ru-gcEJud0clE`,
  "room-joined": `${ADS_TAG_ID}/EyrCCLD3uugcEJud0clE`,
  "populated-room-entered": `${ADS_TAG_ID}/JgBTCLTTu-gcEJud0clE`,
  // A campaign lands on one permanent room and counts the visitor pressing
  // Connect for it — one campaign, one room, one conversion of its own.
  "demo-room-joined": `${ADS_TAG_ID}/Pn0FCNqy8-scEJud0clE`,
  // A campaign of its own pays for the account itself coming into existence.
  "account-signed-up": `${ADS_TAG_ID}/M2AZCMq89escEJud0clE`,
} as const;

export type ConversionName = keyof typeof CONVERSION_LABELS;

function gtag(): Window["gtag"] {
  if (typeof window === "undefined") return undefined;
  return window.gtag;
}

/**
 * Whether advertising data may be sent. A current stored choice wins on its
 * `marketing` decision; with none, the visitor's region default decides.
 */
function storedAdConsent(): boolean | undefined {
  const record = activeConsent();
  if (record && record.version >= CONSENT_VERSION) {
    return record.marketing === "granted";
  }
  return undefined;
}

export async function hasAdConsent(
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  const stored = storedAdConsent();
  if (stored !== undefined) return stored;
  const rule = await fetchRegionRule(fetchImpl);
  return storedAdConsent() ?? rule.defaults.marketing === "granted";
}

/**
 * Reports one conversion, or does nothing at all: no consent, no tag, no
 * report. Never throws — entering a room must not depend on advertising.
 *
 * `email` is the signed-in visitor's address and is optional: supplied, it goes
 * to the tag as enhanced-conversion data (the tag hashes it; never hash it
 * here) so Google can match the conversion to the click. A guest has none, and
 * nothing else personal is ever attached.
 */
export async function reportConversion(
  name: ConversionName,
  fetchImpl: typeof fetch = fetch,
  email?: string | null,
): Promise<void> {
  try {
    const allowed = await hasAdConsent(fetchImpl);
    // A choice can change even while awaiting an already-resolved stored grant.
    if (!(storedAdConsent() ?? allowed)) return;
    const send = gtag();
    if (!send) return;
    try {
      // Global tag parameters outlive events and account changes in this SPA.
      send("set", "user_data", email ? { email } : null);
      send("event", "conversion", { send_to: CONVERSION_LABELS[name] });
    } finally {
      send("set", "user_data", null);
    }
  } catch {
    /* never break the app because of advertising */
  }
}
